import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {shippingMethodDisplayName} from "../../utils/shippingMethodPresentation.js";
import {isChargeTotalComplete} from "../../utils/reportingChargeCompleteness.js";
import {
    addMoney,
    ChannelMixColors,
    formatAxisTooltip,
    formatChartNumber,
    formatNumberItemTooltip,
    KpiCard,
    MetricBasis,
    Panel,
    ReportingPageDescription,
} from "./ReportingLab.jsx";

export function aggregateRows(rows, nameGetter, amountGetter, includeStatuses = false) {
    const rowsByName = new Map();

    rows.forEach((row) => {
        const name = nameGetter(row) || "-";
        const current = rowsByName.get(name) ?? {amount: 0, completed: 0, received: 0, cancelled: 0};
        current.amount += Number(amountGetter(row) ?? 0);
        if (includeStatuses) {
            current.completed += Number(row.completedAmount ?? 0);
            current.received += Number(row.receivedAmount ?? 0);
            current.cancelled += Number(row.cancelledAmount ?? 0);
        }
        rowsByName.set(name, current);
    });

    return [...rowsByName.entries()]
        .map(([name, values]) => ({name, ...values, cancellationRate: values.cancelled / Math.max(1, values.completed + values.received + values.cancelled) * 100}))
        .filter((row) => row.amount > 0)
        .sort((a, b) => b.amount - a.amount);
}

function aggregateMoney(rows, moneyGetter) {
    return rows.reduce((sum, row) => addMoney(sum, moneyGetter(row)), null);
}

function shareOption(rows, chartTextStyle, chartLegendStyle) {
    return {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        tooltip: {
            trigger: "item",
            formatter: formatNumberItemTooltip,
        },
        legend: {bottom: 0, left: "center", width: "92%", ...chartLegendStyle},
        series: [
            {
                type: "pie",
                radius: ["44%", "66%"],
                center: ["50%", "44%"],
                avoidLabelOverlap: true,
                label: {
                    show: true,
                    formatter: (params) => `${params.name}: ${formatChartNumber(params.percent)} %`,
                    ...chartTextStyle,
                },
                labelLine: {show: true, length: 12, length2: 8},
                data: rows.slice(0, 8).map((row) => ({
                    name: row.name,
                    value: row.amount,
                })),
            },
        ],
    };
}

function rankOption(rows, chartTextStyle) {
    return {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 18, bottom: 28, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, formatChartNumber),
        },
        xAxis: {
            type: "value",
            minInterval: 1,
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        yAxis: {
            type: "category",
            data: [...rows].reverse().map((row) => row.name),
            axisLabel: {fontSize: 12, width: 240, overflow: "truncate"},
        },
        series: [
            {
                type: "bar",
                data: [...rows].reverse().map((row, index) => ({
                    value: row.amount,
                    itemStyle: {color: ChannelMixColors[(rows.length - 1 - index) % ChannelMixColors.length]},
                })),
                barMaxWidth: 24,
            },
        ],
    };
}

function RankingTable({rows, label, amountLabel, renderNumber, renderPercentValue, showStatuses = false, t}) {
    return <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
            <thead>
            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                <th className="py-2 pr-4 font-semibold">{label}</th>
                <th className="py-2 text-right font-semibold">{amountLabel}</th>
                {showStatuses && <th className="py-2 pl-4 text-right font-semibold">{t("reporting_lab.orders.completed", {defaultValue: "Dokončené"})}</th>}
                {showStatuses && <th className="py-2 pl-4 text-right font-semibold">{t("reporting_lab.orders.received", {defaultValue: "Přijaté"})}</th>}
                {showStatuses && <th className="py-2 pl-4 text-right font-semibold">{t("reporting_lab.orders.cancelled", {defaultValue: "Stornované"})}</th>}
                {showStatuses && <th className="py-2 pl-4 text-right font-semibold">{t("reporting_lab.orders_lab.cancellation_rate", {defaultValue: "Míra storen"})}</th>}
            </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {rows.map((row) => <tr key={row.name}>
                <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{row.name}</td>
                <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.amount)}</td>
                {showStatuses && <td className="py-3 pl-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.completed)}</td>}
                {showStatuses && <td className="py-3 pl-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.received)}</td>}
                {showStatuses && <td className="py-3 pl-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.cancelled)}</td>}
                {showStatuses && <td className="py-3 pl-4 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(row.cancellationRate)}</td>}
            </tr>)}
            </tbody>
        </table>
    </div>;
}

export default function ReportingDeliveryPaymentsLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const [data, setData] = useState({payments: [], shippings: [], countries: []});
    const [loading, setLoading] = useState(true);
    const {et, retentionExpired} = useReportingError(project.id, setLoading);
    const metricBasis = MetricBasis.Completed;
    const metricBasisMeta = useMemo(() => ({
        [MetricBasis.Marketing]: {
            label: t("reporting_lab.metric_basis.marketing.label", {defaultValue: "Objednávky bez storen"}),
            description: t("reporting_lab.metric_basis.marketing.description", {defaultValue: "Všechny objednávky kromě stornovaných, včetně objednávek bez známého stavu."}),
        },
        [MetricBasis.Completed]: {
            label: t("reporting_lab.metric_basis.business.label", {defaultValue: "Obchodní výkon"}),
            description: t("reporting_lab.metric_basis.business.description", {defaultValue: "Jen dokončené objednávky. Vhodné pro majitele, finance a reálný vyfakturovaný výkon."}),
        },
    }), [t]);

    const filter = useMemo(() => ({
        since: since.format("YYYY-MM-DD"),
        until: until.format("YYYY-MM-DD"),
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
        metricBasis,
    }), [since, until, centresAllowListed, domainsAllowListed, metricBasis]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setData({payments: [], shippings: [], countries: []});

        const api = new ErpCalculationApi(getAuthorityConfiguration());

        api.deliveryPaymentsPerformance({projectId: project.id, deliveryPaymentsFilter: filter})
            .then((response) => {
                if (!active) {
                    return;
                }

                setData({
                    payments: response?.payments ?? [],
                    shippings: response?.shippings ?? [],
                    countries: response?.countries ?? [],
                });
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, filter]);

    const paymentRows = useMemo(() => aggregateRows(data.payments, (row) => row.remoteId === "default"
        ? t("orders.unresolved-payment", {defaultValue: "Neurčená platba"})
        : row.name, (row) => row.amount, true), [data.payments, t]);
    const shippingRows = useMemo(() => aggregateRows(
        data.shippings,
        (row) => shippingMethodDisplayName(row, t),
        (row) => row.amount,
        true,
    ), [data.shippings, t]);
    const countryRows = useMemo(() => aggregateRows(data.countries, (row) => row.countryName === "default"
        ? t("orders.unresolved-country", {defaultValue: "Neurčená země"})
        : row.countryName, (row) => row.amount), [data.countries, t]);

    const orderTotal = paymentRows.reduce((sum, row) => sum + row.amount, 0);
    const paymentChargesComplete = isChargeTotalComplete(data.payments);
    const shippingChargesComplete = isChargeTotalComplete(data.shippings);
    const paymentChargedTotal = paymentChargesComplete ? aggregateMoney(data.payments, (row) => row.chargedPrice) : null;
    const shippingChargedTotal = shippingChargesComplete ? aggregateMoney(data.shippings, (row) => row.chargedPrice) : null;
    const chargedPriceMeta = (complete, total) => `${t("reporting_lab.delivery_payments.charged_price", {defaultValue: "Účtováno"})}: ${complete
        ? renderCurrencyValue(total, true)
        : t("reporting_lab.delivery_payments.charged_price_unavailable", {defaultValue: "nedostupné"})}`;
    const chargedPriceHelp = t("reporting_lab.delivery_payments.charged_price_help", {defaultValue: "Počty objednávek podle metody zůstávají dostupné. Částku účtovanou zákazníkům nelze určit, pokud zdrojové poplatky nebo jejich úpravy nejsou známé."});
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    return <WideSecureLayout periodReport retentionExpired={retentionExpired}
        title={t("reporting_lab.pages.delivery_payments", {defaultValue: "Doprava a platby"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
     intro={<ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.delivery_payments.title", {defaultValue: "K čemu slouží doprava a platby"})}
                >
                    {t("reporting_lab.descriptions.delivery_payments.text", {defaultValue: "Pomáhá pochopit, jaké platební a doručovací metody zákazníci používají, a rychle odhalit neobvyklé změny v mixu objednávek. Grafy pracují s počtem objednávek podle metody, ne s účtovanou cenou ani interním nákladem dopravy nebo platby."})}
                </ReportingPageDescription>}>
        <div className="grid grid-cols-12 gap-4">

                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.delivery_payments.orders", {defaultValue: "Objednávky"})} value={renderNumber(orderTotal)} meta={metricBasisMeta[metricBasis].label}/>
                </div>
                <div className="col-span-12 md:col-span-4">
                    <KpiCard
                        loading={loading}
                        label={t("reporting_lab.delivery_payments.payment_methods", {defaultValue: "Platební metody"})}
                        value={renderNumber(paymentRows.length)}
                        meta={chargedPriceMeta(paymentChargesComplete, paymentChargedTotal)}
                        metaTone={!paymentChargesComplete ? "unavailable" : "default"}
                        info={chargedPriceHelp}
                        infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}
                    />
                </div>
                <div className="col-span-12 md:col-span-4">
                    <KpiCard
                        loading={loading}
                        label={t("reporting_lab.delivery_payments.shipping_methods", {defaultValue: "Dopravy"})}
                        value={renderNumber(shippingRows.length)}
                        meta={chargedPriceMeta(shippingChargesComplete, shippingChargedTotal)}
                        metaTone={!shippingChargesComplete ? "unavailable" : "default"}
                        info={chargedPriceHelp}
                        infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}
                    />
                </div>
                {(!paymentChargesComplete || !shippingChargesComplete) && <div className="app-surface col-span-12 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                    {t("reporting_lab.delivery_payments.charge_incomplete", {defaultValue: "Souhrn účtovaných částek za dopravu nebo platbu není úplný, protože u některých záznamů nelze částku spolehlivě určit. Počty objednávek podle metody zůstávají zobrazené."})}
                </div>}

                <Panel loading={loading} title={t("reporting_lab.delivery_payments.payments_share", {defaultValue: "Podíl platebních metod"})} className="col-span-12 xl:col-span-6">
                    <EChart option={shareOption(paymentRows, chartTextStyle, chartLegendStyle)} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.delivery_payments.shipping_share", {defaultValue: "Podíl dopravy"})} className="col-span-12 xl:col-span-6">
                    <EChart option={shareOption(shippingRows, chartTextStyle, chartLegendStyle)} className="h-96"/>
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.delivery_payments.countries", {defaultValue: "Objednávky podle země"})} className="col-span-12 xl:col-span-7">
                    <EChart option={rankOption(countryRows.slice(0, 10), chartTextStyle)} className="h-80"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.delivery_payments.country_table", {defaultValue: "Přehled zemí"})} className="col-span-12 xl:col-span-5">
                    <RankingTable rows={countryRows.slice(0, 10)} label={t("reporting_lab.delivery_payments.country", {defaultValue: "Země"})} amountLabel={t("reporting_lab.delivery_payments.amount", {defaultValue: "Počet"})} renderNumber={renderNumber} renderPercentValue={renderPercentValue} t={t}/>
                </Panel>

                <div className="col-span-12 text-xs text-slate-500 dark:text-slate-400">
                    {t("reporting_lab.delivery_payments.cancellation_rate_basis", {defaultValue: "Míra storen používá jako jmenovatel všechny přijaté, dokončené a stornované objednávky ve výběru. Přepínač objednávkové báze mění pouze zobrazený počet objednávek."})}
                </div>

                <Panel loading={loading} title={t("reporting_lab.delivery_payments.payment_table", {defaultValue: "Přehled plateb"})} className="col-span-12 xl:col-span-6">
                    <RankingTable rows={paymentRows} label={t("reporting_lab.delivery_payments.payment", {defaultValue: "Platba"})} amountLabel={t("reporting_lab.delivery_payments.amount", {defaultValue: "Počet"})} renderNumber={renderNumber} renderPercentValue={renderPercentValue} showStatuses t={t}/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.delivery_payments.shipping_table", {defaultValue: "Přehled doprav"})} className="col-span-12 xl:col-span-6">
                    <RankingTable rows={shippingRows} label={t("reporting_lab.delivery_payments.shipping", {defaultValue: "Doprava"})} amountLabel={t("reporting_lab.delivery_payments.amount", {defaultValue: "Počet"})} renderNumber={renderNumber} renderPercentValue={renderPercentValue} showStatuses t={t}/>
                </Panel>
            </div>
    </WideSecureLayout>;
}
